import { Stack, styled, useTheme, Typography } from '@mui/material';
import { type CharityVettingNavigationItem } from 'features/charity-vetting/utils/navigationTypes';
import { type KeyboardEvent } from 'react';
import { CheckIcon } from 'ui/SvgIcon';

const StyledMenuItem = styled(Stack, {
  shouldForwardProp: (prop: string) => !['isActive', 'color', 'cursor'].includes(prop),
})<{
  isActive: boolean;
  color: string;
  cursor: string;
}>(({ theme: { palette, spacing, breakpoints }, isActive, color, cursor }) => ({
  backgroundColor: isActive ? palette.cvi.neutrals[10] : palette.cvi.primary[30],
  color: isActive ? palette.cvi.primary[30] : color,
  width: '100%',
  padding: spacing(2),
  flexDirection: 'row',
  alignItems: 'center',
  gap: spacing(1.5),
  position: 'relative',
  left: spacing(0.1),
  borderRadius: spacing(1.5),
  cursor: cursor,
  '&:focus': {
    outline: 'none',
  },
  '&:focus-visible': {
    outline: `2px solid ${palette.cvi.primary[2]}`,
  },
  [breakpoints.up('md')]: {
    borderRadius: spacing(1.5, 0, 0, 1.5),
    '&::before, &::after': {
      content: '""',
      position: 'absolute',
      width: spacing(1.5),
      height: spacing(2.25),
      backgroundColor: isActive ? palette.cvi.neutrals[10] : palette.cvi.primary[30],
      right: 0,
    },
    '&::before': {
      top: spacing(-2.2),
      clipPath: 'path("M0,18 H12 V0 Q12,18 0,18 Z")',
    },
    '&::after': {
      bottom: spacing(-2.2),
      clipPath: 'path("M0,0 H12 V18 Q12,0 0,0 Z")',
    },
  },
}));

const StyledNumber = styled(Stack, {
  shouldForwardProp: (prop: string) => !['isActive', 'color', 'bgColor'].includes(prop),
})<{
  isActive?: boolean;
  color: string;
  bgColor: string;
}>(({ theme: { palette, spacing }, isActive, color, bgColor }) => ({
  backgroundColor: isActive ? palette.cvi.primary[30] : bgColor,
  color: color,
  width: spacing(4),
  height: spacing(4),
  borderRadius: '50%',
  flexDirection: 'row',
  justifyContent: 'center',
  alignItems: 'center',
}));

const StyledContent = styled(Stack)(({ theme: { breakpoints } }) => ({
  maxWidth: 220,
  [breakpoints.down('md')]: {
    maxWidth: 212,
  },
}));

interface MenuItemProps {
  item: CharityVettingNavigationItem;
  onClick: (item: CharityVettingNavigationItem) => void;
  onFocusNavigation: (direction: 'up' | 'down') => void;
  stepNumber: number;
  isClickable: boolean;
}

export const MenuItem = ({
  item,
  onClick,
  onFocusNavigation,
  stepNumber,
  isClickable,
}: MenuItemProps) => {
  const { stepLabel, isActive, isCompleted } = item;
  const theme = useTheme();
  const {
    palette: { cvi },
  } = theme;
  const isUnInitiated = !isActive && !isCompleted;

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick(item);
    }

    if (e.key === 'ArrowUp') {
      onFocusNavigation('up');
    }
    if (e.key === 'ArrowDown') {
      onFocusNavigation('down');
    }
  };

  const handleOnClick = () => onClick(item);

  return (
    <StyledMenuItem
      tabIndex={0}
      isActive={isActive}
      aria-selected={isActive || undefined}
      cursor={isClickable ? 'pointer' : 'default'}
      color={isUnInitiated ? cvi.green[10] : cvi.neutrals[10]}
      onClick={handleOnClick}
      onKeyDown={onKeyDown}
    >
      <StyledNumber
        isActive={isActive}
        color={isUnInitiated ? cvi.primary[30] : cvi.neutrals[10]}
        bgColor={isUnInitiated ? cvi.green[10] : cvi.primary[20]}
      >
        {isCompleted ? <CheckIcon /> : <Typography variant='subtitle1'>{stepNumber}</Typography>}
      </StyledNumber>
      <StyledContent>
        <Typography variant='bodyBold1'>{stepLabel}</Typography>
      </StyledContent>
    </StyledMenuItem>
  );
};
